import React from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Plus, X, Settings, Wallet } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, Badge } from "../../ui";
import { ShadcnButton } from "../../ui";
import { ErrorAlert } from "../../ui/ErrorAlert";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "../../ui/select";
import { Accordion, AccordionItem } from "../../ui/Accordion";
import { PriceInput } from "../../form";
import { DateField } from "../fields/DateField";
import {
 formatCurrency,
 parseCurrency,
 formatDateToBR,
} from "../../../utils/formatters";
import { cn } from "../../../utils/cn";
import { Vehicle } from "../../../types";

interface PaymentPlan {
 paymentMethodId: string;
 optionId?: string;
 value: number;
 installments?: number;
 date?: string;
}

interface PaymentForm {
 id: string;
 name: string;
 description?: string;
 options?: Array<{
 id: string;
 paymentFormId: string;
 name: string;
 description?: string;
 installments: number;
 interestRate?: string | number;
 isActive?: boolean;
 }>;
}

interface PaymentsSectionProps {
 savedPaymentPlans: PaymentPlan[];
 tempPaymentMethod: string;
 tempPaymentOption: string;
 tempPaymentAmountFormatted: string;
 paymentForms: PaymentForm[];
 isLoadingPaymentData: boolean;
 selectedVehicle: Vehicle | null;
 financingOptionsList: Array<{ value: string; label: string }>;
 paymentMethodsOptions: Array<{ value: string; label: string }>;
 onSavedPaymentPlansChange: (plans: PaymentPlan[]) => void;
 onTempPaymentMethodChange: (methodId: string) => void;
 onTempPaymentOptionChange: (optionId: string) => void;
 onTempPaymentAmountChange: (formatted: string) => void;
 calculateRemainingAmount: () => number;
 calculatePaymentWithInterest: (
 baseValue: number,
 installments: number,
 interestRate?: string | number
 ) => { totalWithInterest: number; installmentValue: number };
 handleAddPaymentPlan: () => void;
}

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
 savedPaymentPlans,
 tempPaymentMethod,
 tempPaymentOption,
 tempPaymentAmountFormatted,
 paymentForms,
 isLoadingPaymentData,
 selectedVehicle,
 financingOptionsList,
 paymentMethodsOptions,
 onSavedPaymentPlansChange,
 onTempPaymentMethodChange,
 onTempPaymentOptionChange,
 onTempPaymentAmountChange,
 calculateRemainingAmount,
 calculatePaymentWithInterest,
 handleAddPaymentPlan,
}) => {
 const navigate = useNavigate();

 const handleRemovePaymentPlan = (index: number) => {
 const updated = savedPaymentPlans.filter((_, i) => i !== index);
 onSavedPaymentPlansChange(updated);
 };

 const handleUpdatePaymentPlanValue = (index: number, value: number) => {
 const numericValue =
 typeof value === "string"
 ? parseFloat(String(value)) || 0
 : Number(value);
 if (!Number.isFinite(numericValue) || numericValue < 0) return;
 const updated = savedPaymentPlans.map((p, i) =>
 i === index ? { ...p, value: numericValue } : p
 );
 onSavedPaymentPlansChange(updated);
 };

 const handleUpdatePaymentPlanDate = (index: number, date: string) => {
 const updated = savedPaymentPlans.map((p, i) =>
 i === index ? { ...p, date } : p
 );
 onSavedPaymentPlansChange(updated);
 };

 const handleUseRemainingAmount = (index: number) => {
 const remaining = calculateRemainingAmount();
 if (remaining > 0) {
 const currentValue = savedPaymentPlans[index]?.value || 0;
 handleUpdatePaymentPlanValue(index, currentValue + remaining);
 }
 };

 return (
 <Card className="overflow-hidden border-border shadow-sm">
 <CardHeader className="border-b border-border bg-muted/20 pb-4">
 <CardTitle className="flex items-center gap-2 text-lg font-display">
 <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
 <CreditCard className="h-5 w-5" />
 </span>
 Pagamentos
 </CardTitle>
 <p className="text-sm text-muted-foreground font-normal mt-1 pl-11">
 Combine formas de pagamento até cobrir o valor final da proposta.
 </p>
 </CardHeader>
 <CardContent className="space-y-6 pt-6">
 {paymentMethodsOptions.length === 0 && !isLoadingPaymentData ? (
 <div className="space-y-3">
 <ErrorAlert
 type="warning"
 title="Nenhum método de pagamento cadastrado"
 message="Você precisa cadastrar métodos de pagamento nas configurações antes de criar uma proposta."
 />
 <ShadcnButton
 type="button"
 variant="outline"
 onClick={() => navigate("/settings/payment-forms")}
 className="w-full"
 >
 <Settings className="h-4 w-4 mr-2" />
 Ir para Configurações de Pagamento
 </ShadcnButton>
 </div>
 ) : (
 <div
 className={cn(
 "rounded-xl border border-border bg-muted/25 p-4 sm:p-5 space-y-4",
 "ring-1 ring-border/60"
 )}
 >
 <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
 Novo pagamento
 </p>
 <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
 <div className="space-y-2 sm:col-span-2">
 <label className="text-sm font-medium text-foreground">
 Forma de pagamento
 </label>
 <Select
 value={tempPaymentMethod}
 onValueChange={(value) => {
 onTempPaymentMethodChange(value);
 onTempPaymentOptionChange("");
 }}
 disabled={
 isLoadingPaymentData || paymentMethodsOptions.length === 0
 }
 >
 <SelectTrigger className="bg-card">
 <SelectValue placeholder="Selecione a forma de pagamento" />
 </SelectTrigger>
 <SelectContent>
 {paymentMethodsOptions.map((option) => (
 <SelectItem key={option.value} value={option.value}>
 {option.label}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 {financingOptionsList.length > 0 && (
 <div className="space-y-2 sm:col-span-2">
 <label className="text-sm font-medium text-foreground">
 Opção de parcelamento
 </label>
 <Select
 value={tempPaymentOption}
 onValueChange={(value) => onTempPaymentOptionChange(value)}
 disabled={isLoadingPaymentData || !tempPaymentMethod}
 >
 <SelectTrigger className="bg-card">
 <SelectValue placeholder="Selecione a opção (opcional)" />
 </SelectTrigger>
 <SelectContent>
 {financingOptionsList.map((option) => (
 <SelectItem key={option.value} value={option.value}>
 {option.label}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 )}

 <div className="space-y-2 sm:col-span-2">
 <label className="text-sm font-medium text-foreground flex items-center gap-2">
 <Wallet className="h-4 w-4 text-muted-foreground" />
 Valor deste pagamento
 </label>
 <PriceInput
 value={tempPaymentAmountFormatted}
 onChange={onTempPaymentAmountChange}
 placeholder="R$ 0,00"
 className="bg-card"
 />
 <p className="text-xs text-muted-foreground">
 Opcional: informe antes de adicionar ou ajuste depois na lista abaixo.
 </p>
 </div>
 </div>

 <ShadcnButton
 type="button"
 onClick={handleAddPaymentPlan}
 disabled={
 !tempPaymentMethod || paymentMethodsOptions.length === 0
 }
 className="w-full sm:w-auto min-w-[160px]"
 >
 <Plus className="h-4 w-4 mr-2" />
 Adicionar à proposta
 </ShadcnButton>
 </div>
 )}

 {savedPaymentPlans.length > 0 && (
 <div className="space-y-3">
 <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
 Pagamentos adicionados
 </p>
 <Accordion>
 {savedPaymentPlans.map((plan, index) => {
 const paymentMethod = paymentForms.find(
 (form) => form.id === plan.paymentMethodId
 );
 const selectedForm = paymentForms.find(
 (form) => form.id === plan.paymentMethodId
 );
 const optionDetails = selectedForm?.options?.find(
 (opt) => opt.id === plan.optionId
 );

 const installments =
 plan.installments || optionDetails?.installments || 1;
 const calculation = calculatePaymentWithInterest(
 plan.value,
 installments,
 optionDetails?.interestRate
 );

 const dateFormatted = plan.date ? formatDateToBR(plan.date) : "";
 const installmentsText = installments > 1 ? ` · ${installments}x` : "";
 const dateText = dateFormatted ? ` · ${dateFormatted}` : "";
 const needsValue = !plan.value || plan.value <= 0;

 return (
 <AccordionItem
 key={`saved-${plan.paymentMethodId}-${index}-${plan.optionId ?? "none"}`}
 defaultOpen={needsValue}
 title={
 <div className="flex flex-wrap items-center gap-x-3 gap-y-2 w-full pr-6 text-left">
 <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
 <span className="font-medium text-foreground truncate">
 {paymentMethod?.name || "Método não encontrado"}
 </span>
 {needsValue && (
 <Badge variant="warning" size="sm">
 Pendente
 </Badge>
 )}
 </div>
 <span className="font-semibold text-foreground tabular-nums shrink-0">
 {formatCurrency(plan.value)}
 </span>
 <span className="text-xs text-muted-foreground hidden md:inline shrink-0 max-w-[10rem] truncate">
 {installmentsText}
 {dateText}
 </span>
 <button
 type="button"
 onClick={(e) => {
 e.stopPropagation();
 handleRemovePaymentPlan(index);
 }}
 className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded-md hover:bg-destructive/10"
 title="Excluir"
 >
 <X className="h-4 w-4" />
 </button>
 </div>
 }
 >
 <div className="space-y-5 pt-1 rounded-lg bg-muted/15 -mx-1 px-3 py-3 border border-border/60">
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Valor total
 </label>
 <div className="flex flex-col sm:flex-row gap-2">
 <PriceInput
 value={
 plan.value > 0 ? formatCurrency(plan.value) : ""
 }
 onChange={(formattedValue) => {
 handleUpdatePaymentPlanValue(
 index,
 parseCurrency(formattedValue)
 );
 }}
 placeholder="R$ 0,00"
 className="flex-1 bg-card"
 />
 {selectedVehicle && (
 <ShadcnButton
 type="button"
 variant="outline"
 size="sm"
 onClick={() => handleUseRemainingAmount(index)}
 disabled={calculateRemainingAmount() <= 0}
 className="whitespace-nowrap shrink-0"
 title="Somar o valor restante ao valor atual"
 >
 Usar restante
 </ShadcnButton>
 )}
 </div>
 </div>

 <DateField
 label="Data do pagamento"
 value={plan.date || ""}
 onChange={(isoDate) => handleUpdatePaymentPlanDate(index, isoDate)}
 placeholder="Selecione a data do pagamento"
 />

 <div className="space-y-3 p-4 bg-card rounded-xl border border-border text-sm">
 {installments > 1 && (
 <div className="flex justify-between items-center">
 <span className="text-muted-foreground">Parcelas</span>
 <span className="font-medium text-foreground">{installments}x</span>
 </div>
 )}
 {optionDetails?.interestRate != null &&
 String(optionDetails.interestRate) !== "" && (
 <div className="flex justify-between items-center">
 <span className="text-muted-foreground">Taxa de juros</span>
 <span className="font-medium text-foreground">
 {optionDetails.interestRate}% a.m.
 </span>
 </div>
 )}
 {optionDetails?.interestRate != null &&
 String(optionDetails.interestRate) !== "" && (
 <div className="flex justify-between items-center pt-2 border-t border-border">
 <span className="text-muted-foreground">Total com juros</span>
 <span className="font-semibold text-accent">
 {formatCurrency(calculation.totalWithInterest)}
 </span>
 </div>
 )}
 <div className="flex justify-between items-center pt-2 border-t border-border">
 <span className="text-muted-foreground">Valor da parcela</span>
 <span className="font-bold text-accent tabular-nums">
 {formatCurrency(calculation.installmentValue)}
 </span>
 </div>
 </div>
 </div>
 </AccordionItem>
 );
 })}
 </Accordion>
 </div>
 )}
 </CardContent>
 </Card>
 );
};
